import { useContext, useState, useRef } from "react";
import { useForm } from "react-hook-form";

import {
  Button,
  Box,
  TextField,
  Typography,
  Dialog,
  Checkbox,
  FormControlLabel,
  DialogActions,
  DialogContent,
} from "@mui/material";

import Edit from '@mui/icons-material/Edit';

import moment from "moment";

import { ReloadContext } from "../../context/ReloadContext";
import { getSearchBaitaiShubetsuId, updateBaitaiShubetsu } from "../../api/baitaiShubetsu";
import LoadingButton from '@mui/lab/LoadingButton';


interface Props {
  BaitaiShubetsuId: number;
}

/**
 * 媒体種別設定ダイアログコンポーネント
 *
 * @param {Props} { BaitaiShubetsuId }
 * @return {*} 
 */
export const BaitaiShubetsuEditDialog = ({ BaitaiShubetsuId }: Props) => {
  const reloadContext = useContext(ReloadContext);
  const [open, setOpen] = useState(false);
  const { handleSubmit } = useForm<any>();

  const [shubetsuMei, setBaitaiShubetsu] = useState<string>("");
  const [gamenSu, setGamenSu] = useState<number>(1);
  const [ryomenHoeiKahiFlg, setRyomenHoeiKahiFlg] = useState<boolean>(false);
  const [updateDt, setUpdateDt] = useState<Date | null>(null);
  const [load, setLoad] = useState<boolean>(false);

  // エラーメッセージ表示用
  // 各入力項目ごとのメッセージ
  const [errorMessageBaitaiShubetsu, setErrorMessageBaitaiShubetsu] = useState<string>("");
  const [errorMessageGamenSu, setErrorMessageGamenSu] = useState<string>("");

  const isCreate = BaitaiShubetsuId === 0;
  // 処理中フラグ（ボタン連打対策）
  const processing = useRef(false);

  const handleClickOpen = async () => {
    // エラーメッセージ初期化
    initErrorMessage();

    if (isCreate) { // 新規登録
      setBaitaiShubetsu("");
      setGamenSu(1);
      setRyomenHoeiKahiFlg(false);
      setUpdateDt(null);
    } else {
      // 媒体種別情報取得
      const res = await getSearchBaitaiShubetsuId(BaitaiShubetsuId);
      if (res?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: res?.message,
        });
      }

      setBaitaiShubetsu(res.data[0].shubetsu_mei);
      setGamenSu(res.data[0].gamen_su);
      setRyomenHoeiKahiFlg(res.data[0].ryomen_hoei_kahi_flg);
      setUpdateDt(res.data[0].update_dt);
    }

    setOpen(true);
  };

  /**
 * エラーメッセージ初期化処理
 */
  const initErrorMessage = () => {
    setErrorMessageBaitaiShubetsu("")
    setErrorMessageGamenSu("")
  }

  const handleClose = () => {
    setOpen(false);
  };

  // テーブル更新・一覧更新
  const handleUpdate = async () => {
    // エラーメッセージ初期化
    initErrorMessage();

    if (shubetsuMei === "") {
      return setErrorMessageBaitaiShubetsu("媒体種別を入力してください。");
    }
    if (gamenSu < 1 || gamenSu > 4) {
      return setErrorMessageGamenSu("画面数は1～4を入力してください。");
    }

    // 処理中(true)なら非同期処理せずに抜ける
    if (processing.current) return;
    // 処理中フラグを上げる
    processing.current = true;
    // 疑似非同期処理
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 1000);
    setLoad(true);
    const result = await updateBaitaiShubetsu({
      id: BaitaiShubetsuId,
      shubetsu_mei: shubetsuMei,
      gamen_su: gamenSu,
      ryomen_hoei_kahi_flg: ryomenHoeiKahiFlg,
      update_dt: (updateDt) ? moment(updateDt).format("YYYY-MM-DD HH:mm:ss") : undefined,
    });

    await reloadContext?.setSnackbarInfo({
      isOpen: true,
      type: result.isError ? "error" : "success",
      message: result.message,
    });
    await reloadSetting();
    setLoad(false);
  };

  // モーダル→画面戻り時
  const reloadSetting = () => {
    reloadContext?.setReload(reloadContext?.reload + 1);
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {
        isCreate ?
          <Button
            type="button"
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            onClick={() => handleClickOpen()}
          >
            新規媒体種別登録
          </Button>
          :
          <Edit color="primary" fontSize='large'
            onClick={() => handleClickOpen()}
          />
      }
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            width: "50vw",
          }
        }}
      >
        <form onSubmit={handleSubmit(handleUpdate)}>
          <Typography component="h3" variant="h6" mt={1} ml={2}>
            {isCreate ? "新規媒体種別登録" : "媒体種別情報編集"}
          </Typography>
          <DialogContent sx={{ paddingTop: 0 }}>
            <TextField
              label="媒体種別名 *"
              value={shubetsuMei}
              onChange={e => setBaitaiShubetsu(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ marginTop: 1 }}
            />
            {errorMessageBaitaiShubetsu && (
              <small className={"error-message"}>{errorMessageBaitaiShubetsu}</small>
            )}
            <TextField
              label="画面数 *"
              type="number"
              value={gamenSu}
              disabled={isCreate ? false : true}
              onChange={e => setGamenSu(+e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ marginTop: 1 }}
            />
            {errorMessageGamenSu && (
              <Typography >
                <small className={"error-message"}>{errorMessageGamenSu}</small>
              </Typography>
            )}
            {/* v1.1.4 両面放映を一旦廃止。要望があれば対応する。 */}
            {/* <FormControlLabel
              control={
                <Checkbox checked={ryomenHoeiKahiFlg} onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRyomenHoeiKahiFlg(event.target.checked);
                }}
                />
              }
              label="両面放映"
              sx={{ marginTop: 1 }}
            /> */}

          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>閉じる</Button>
            <LoadingButton loading={load} variant="contained" type="submit">登録</LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
